import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { BoardFloorPlanData } from '../../../types';
import { uploadBoardImage } from '../../../utils/leadBoardService';
import { renderPdfPageToFile } from '../../../utils/boardSnapshotCapture';
import BoardSnapshotPicker, {
  type BoardSnapshotModelChoice,
  type BoardSnapshotPdfChoice,
} from '../BoardSnapshotPicker';
import HtmlViewerModal from '../../html/HtmlViewerModal';
import type { FloorPlanCatalogGroup } from './floorPlanCatalog';
import FloorPlanCanvas, { type FloorPlanPresenceAuth } from './FloorPlanCanvas';
import FloorPlanInspector from './FloorPlanInspector';
import FloorPlanLayers from './FloorPlanLayers';
import FloorPlanToolbar from './FloorPlanToolbar';
import { saveFloorPlanLive } from '../../../utils/floorPlanLiveService';
import { dropConstraintsForWall, inferConstraints } from './floorPlanConstraints';
import {
  ackDirtyIds,
  applyOmittedDiff,
  dirtyEntityIds,
  floorPlanEntityCount,
  liveDropsEntities,
  planSignature,
  preferSavedFloorPlan,
  pruneConfirmedOmissions,
  restoreDroppedLocalEntities,
  threeWayMergeFloorPlan,
  type FloorPlanOmittedIds,
} from './floorPlanMerge';
import {
  clonePlan,
  createDefaultFloorPlan,
  createUnderlay,
  formatInchesFtIn,
  parseDimensionToInches,
  resizeFootprint,
  rotateItem,
  setUnderlayHidden,
} from './floorPlanMath';
import type { FloorPlanScaleRef, FloorPlanSelection, FloorPlanThickness, FloorPlanTool } from './floorPlanTypes';
import { loadSnapEnabled, saveSnapEnabled } from '../boardSnap';
import { downloadFloorPlanPdf } from './floorPlanPdfExport';
import type { FloorPlanPdfPaper } from './floorPlanPdf';
import './FloorPlanEditor.css';

interface FloorPlanEditorProps {
  open: boolean;
  dealId: string;
  nodeId?: string;
  timberProjectId?: string;
  initial?: BoardFloorPlanData;
  /** Latest live plan from Firestore; merged into the local draft. */
  live?: BoardFloorPlanData | null;
  liveRev?: number;
  onClose: () => void;
  onSubmit: (data: BoardFloorPlanData) => void;
  onAutosave?: (data: BoardFloorPlanData) => void;
  presence?: FloorPlanPresenceAuth | null;
}

function openedPlan(initial?: BoardFloorPlanData): BoardFloorPlanData {
  const next = initial ? clonePlan(initial) : createDefaultFloorPlan();
  if (initial && next.walls.length && initial.constraints == null) {
    next.constraints = inferConstraints(next.walls);
  }
  return next;
}

const FloorPlanEditor: React.FC<FloorPlanEditorProps> = ({
  open,
  dealId,
  nodeId,
  timberProjectId,
  initial,
  live,
  liveRev = 0,
  onClose,
  onSubmit,
  onAutosave,
  presence,
}) => {
  const [draft, setDraft] = useState<BoardFloorPlanData>(() => openedPlan(initial));
  const [viewEpoch, setViewEpoch] = useState(0);
  const [tool, setTool] = useState<FloorPlanTool>('wall');
  const [thickness, setThickness] = useState<FloorPlanThickness>(6);
  const [placeCatalogId, setPlaceCatalogId] = useState<string | null>(null);
  const [catalogGroup, setCatalogGroup] = useState<FloorPlanCatalogGroup>('furniture');
  const [selected, setSelected] = useState<FloorPlanSelection | null>(null);
  const [scaleRef, setScaleRef] = useState<FloorPlanScaleRef | null>(null);
  const [widthDraft, setWidthDraft] = useState("36'");
  const [depthDraft, setDepthDraft] = useState("24'");
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [snapEnabled, setSnapEnabled] = useState(loadSnapEnabled);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [htmlCaptureDoc, setHtmlCaptureDoc] = useState<BoardSnapshotModelChoice | null>(null);
  const htmlCaptureCountRef = useRef(0);
  const undoRef = useRef<BoardFloorPlanData[]>([]);
  const redoRef = useRef<BoardFloorPlanData[]>([]);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const baseRef = useRef<BoardFloorPlanData>(draft);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedSigRef = useRef('');
  const lastLiveSigRef = useRef('');
  const writingLiveRef = useRef(false);
  const persistPendingRef = useRef<BoardFloorPlanData | null>(null);
  const writeGenRef = useRef(0);
  const lastAckedRevRef = useRef(0);
  const applyingLiveRef = useRef(false);
  const persistRef = useRef<(next: BoardFloorPlanData) => void>(() => undefined);
  const wasOpenRef = useRef(false);
  const omittedRef = useRef<FloorPlanOmittedIds>({});
  const dirtyRef = useRef<FloorPlanOmittedIds>({});
  const staleSigsRef = useRef<Record<string, true>>({});
  const prevDraftRef = useRef(draft);
  const prevCountsRef = useRef({ items: 0, walls: 0, openings: 0, measures: 0 });

  const persist = useCallback(
    (next: BoardFloorPlanData) => {
      if (
        floorPlanEntityCount(baseRef.current) > 0 &&
        floorPlanEntityCount(next) === 0 &&
        Object.keys(omittedRef.current).length === 0
      ) {
        return;
      }
      const sig = planSignature(next);
      onAutosave?.(clonePlan(next));
      if (!nodeId) {
        lastSavedSigRef.current = sig;
        return;
      }
      if (writingLiveRef.current) {
        persistPendingRef.current = clonePlan(next);
        return;
      }
      if (sig === lastSavedSigRef.current) return;
      lastSavedSigRef.current = sig;
      writingLiveRef.current = true;
      const gen = writeGenRef.current + 1;
      writeGenRef.current = gen;
      const sent = clonePlan(next);
      staleSigsRef.current[planSignature(baseRef.current)] = true;
      void Promise.resolve(
        saveFloorPlanLive(
          dealId,
          nodeId,
          sent,
          baseRef.current,
          presence?.name || presence?.uid,
          omittedRef.current,
          { ...dirtyEntityIds(baseRef.current, sent), ...dirtyRef.current },
        ),
      ).then((result) => {
          if (gen !== writeGenRef.current || persistPendingRef.current) return;
          const merged = result?.data;
          if (!merged?.footprint) return;
          const mergedSig = planSignature(merged);
          lastLiveSigRef.current = mergedSig;
          lastSavedSigRef.current = mergedSig;
          lastAckedRevRef.current = Math.max(lastAckedRevRef.current, Number(result?.rev || 0));
          delete staleSigsRef.current[mergedSig];
        })
        .catch((err) => {
          console.warn('[FloorPlanEditor] live save failed', err);
        })
        .finally(() => {
          if (gen !== writeGenRef.current) return;
          writingLiveRef.current = false;
          const queued = persistPendingRef.current;
          persistPendingRef.current = null;
          if (queued) persistRef.current(queued);
        });
    },
    [dealId, nodeId, onAutosave, presence?.name, presence?.uid],
  );
  persistRef.current = persist;

  const flushAutosave = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    persist(draftRef.current);
  }, [persist]);

  useLayoutEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    const source = preferSavedFloorPlan(initial, live && live.footprint ? live : null);
    const next = openedPlan(source);
    setDraft(next);
    setWidthDraft(formatInchesFtIn(next.footprint.widthIn));
    setDepthDraft(formatInchesFtIn(next.footprint.depthIn));
    setTool(next.walls.length ? 'select' : 'wall');
    setThickness(next.walls.length ? 4 : 6);
    setPlaceCatalogId(null);
    setSelected(null);
    setScaleRef(null);
    setSnapshotOpen(false);
    setHtmlCaptureDoc(null);
    undoRef.current = [];
    redoRef.current = [];
    lastSavedSigRef.current =
      !live && initial && initial.constraints == null && next.constraints?.length
        ? ''
        : planSignature(next);
    lastLiveSigRef.current = source ? planSignature(source) : lastSavedSigRef.current;
    baseRef.current = clonePlan(source && source.footprint ? source : next);
    omittedRef.current = {};
    dirtyRef.current = {};
    staleSigsRef.current = {};
    persistPendingRef.current = null;
    writingLiveRef.current = false;
    writeGenRef.current += 1;
    lastAckedRevRef.current = liveRev || 0;
    applyingLiveRef.current = false;
    prevDraftRef.current = next;
    prevCountsRef.current = {
      items: next.items.length,
      walls: next.walls.length,
      openings: next.openings.length,
      measures: (next.measures || []).length,
    };
    setViewEpoch((n) => n + 1);
  }, [open, initial, live, liveRev]);

  useEffect(() => {
    if (!open || !live) return;
    const remoteSig = planSignature(live);
    if ((liveRev || 0) < lastAckedRevRef.current) return;
    if (
      liveDropsEntities(draftRef.current, live) &&
      floorPlanEntityCount(live) < floorPlanEntityCount(draftRef.current)
    ) {
      return;
    }
    if (staleSigsRef.current[remoteSig] && remoteSig !== lastSavedSigRef.current) {
      return;
    }
    dirtyRef.current = ackDirtyIds(dirtyRef.current, draftRef.current, live);
    const dirty = { ...dirtyEntityIds(baseRef.current, draftRef.current), ...dirtyRef.current };
    const pending = Object.keys(dirty).length > 0 || Object.keys(omittedRef.current).length > 0;
    if (remoteSig === lastLiveSigRef.current || remoteSig === lastSavedSigRef.current) {
      lastLiveSigRef.current = remoteSig;
      lastAckedRevRef.current = Math.max(lastAckedRevRef.current, liveRev || 0);
      omittedRef.current = pruneConfirmedOmissions(omittedRef.current, live);
      if (!writingLiveRef.current && !pending) {
        baseRef.current = clonePlan(live);
      }
      return;
    }
    const merged = restoreDroppedLocalEntities(
      threeWayMergeFloorPlan(baseRef.current, draftRef.current, live, omittedRef.current, dirty),
      draftRef.current,
      dirty,
      omittedRef.current,
    );
    omittedRef.current = pruneConfirmedOmissions(omittedRef.current, live);
    dirtyRef.current = ackDirtyIds(dirtyRef.current, merged, live);
    lastAckedRevRef.current = Math.max(lastAckedRevRef.current, liveRev || 0);
    if (!writingLiveRef.current && Object.keys(dirtyRef.current).length === 0 && Object.keys(omittedRef.current).length === 0) {
      lastLiveSigRef.current = remoteSig;
      baseRef.current = clonePlan(live);
    }
    if (planSignature(merged) !== planSignature(draftRef.current)) {
      applyingLiveRef.current = true;
      setDraft(merged);
    }
  }, [live, liveRev, open]);

  useEffect(() => {
    if (!open) return;
    const sig = planSignature(draft);
    const prevDraft = prevDraftRef.current;
    if (applyingLiveRef.current) {
      applyingLiveRef.current = false;
      prevDraftRef.current = draft;
      prevCountsRef.current = {
        items: draft.items.length,
        walls: draft.walls.length,
        openings: draft.openings.length,
        measures: (draft.measures || []).length,
      };
      return;
    }
    omittedRef.current = applyOmittedDiff(omittedRef.current, prevDraft, draft);
    dirtyRef.current = { ...dirtyRef.current, ...dirtyEntityIds(baseRef.current, draft) };
    if (planSignature(prevDraft) !== planSignature(draft)) {
      staleSigsRef.current[planSignature(prevDraft)] = true;
      delete staleSigsRef.current[planSignature(draft)];
    }
    prevDraftRef.current = draft;
    if (sig === lastSavedSigRef.current) return;
    const added =
      draft.items.length > prevCountsRef.current.items ||
      draft.walls.length > prevCountsRef.current.walls ||
      draft.openings.length > prevCountsRef.current.openings ||
      (draft.measures || []).length > prevCountsRef.current.measures;
    const removed =
      draft.items.length < prevCountsRef.current.items ||
      draft.walls.length < prevCountsRef.current.walls ||
      draft.openings.length < prevCountsRef.current.openings ||
      (draft.measures || []).length < prevCountsRef.current.measures ||
      (draft.underlays || []).length < (prevDraft.underlays || []).length;
    prevCountsRef.current = {
      items: draft.items.length,
      walls: draft.walls.length,
      openings: draft.openings.length,
      measures: (draft.measures || []).length,
    };
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    if (added || removed) {
      autosaveTimerRef.current = null;
      persist(draftRef.current);
      return;
    }
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      persist(draftRef.current);
    }, 250);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [draft, open, persist]);

  const beginEdit = useCallback(() => {
    undoRef.current = [...undoRef.current, clonePlan(draftRef.current)].slice(-40);
    redoRef.current = [];
  }, []);

  const commit = useCallback((next: BoardFloorPlanData) => {
    beginEdit();
    setDraft(next);
  }, [beginEdit]);

  const undo = useCallback(() => {
    const prev = undoRef.current.pop();
    if (!prev) return;
    redoRef.current.push(clonePlan(draftRef.current));
    setDraft(prev);
  }, []);

  const redo = useCallback(() => {
    const next = redoRef.current.pop();
    if (!next) return;
    undoRef.current.push(clonePlan(draftRef.current));
    setDraft(next);
  }, []);

  const deleteSelected = useCallback(() => {
    if (!selected) return;
    if (selected.kind === 'wall') {
      commit({
        ...draft,
        walls: draft.walls.filter((w) => w.id !== selected.id),
        openings: draft.openings.filter((o) => o.wallId !== selected.id),
        constraints: dropConstraintsForWall(draft.constraints, selected.id),
      });
    } else if (selected.kind === 'opening') {
      commit({ ...draft, openings: draft.openings.filter((o) => o.id !== selected.id) });
    } else if (selected.kind === 'underlay') {
      commit({
        ...draft,
        underlays: (draft.underlays || []).filter((underlay) => underlay.id !== selected.id),
      });
      setScaleRef(null);
    } else if (selected.kind === 'measure') {
      commit({
        ...draft,
        measures: (draft.measures || []).filter((measure) => measure.id !== selected.id),
      });
    } else {
      commit({ ...draft, items: draft.items.filter((i) => i.id !== selected.id) });
    }
    setSelected(null);
  }, [commit, draft, selected]);

  useEffect(() => {
    setWidthDraft(formatInchesFtIn(draft.footprint.widthIn));
    setDepthDraft(formatInchesFtIn(draft.footprint.depthIn));
  }, [draft.footprint.widthIn, draft.footprint.depthIn]);

  const addUnderlayFromFile = useCallback(
    async (file: File, label: string, opts: { startScale?: boolean } = {}) => {
      const uploaded = await uploadBoardImage(dealId, file);
      const underlay = createUnderlay({
        url: uploaded.url,
        storagePath: uploaded.storagePath,
        label,
        naturalWidth: uploaded.naturalWidth,
        naturalHeight: uploaded.naturalHeight,
        footprint: draftRef.current.footprint,
      });
      const existing = draftRef.current.underlays || [];
      const offset = existing.length * 12;
      const placed = { ...underlay, x: underlay.x + offset, y: underlay.y + offset };
      beginEdit();
      const next = { ...draftRef.current, underlays: [...existing, placed] };
      setDraft(next);
      setSelected({ kind: 'underlay', id: placed.id });
      setTool('select');
      if (opts.startScale !== false) {
        setScaleRef({ underlayId: placed.id, a: null, b: null });
      }
    },
    [beginEdit, dealId],
  );

  const handleAddImages = useCallback(
    async (files: File[]) => {
      const images = files.filter((file) => file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name));
      if (images.length === 0) {
        window.alert('Choose a photo or scan (JPG, PNG, WebP, or HEIC).');
        return;
      }
      setImporting(true);
      setImportProgress({ current: 0, total: images.length });
      try {
        for (let i = 0; i < images.length; i += 1) {
          const file = images[i];
          setImportProgress({ current: i + 1, total: images.length });
          const label = file.name.replace(/\.[^.]+$/, '') || 'Image';
          await addUnderlayFromFile(file, label, { startScale: false });
        }
      } catch (err) {
        console.error('[FloorPlanEditor] image import failed:', err);
        window.alert('Could not add one or more images to the floor plan.');
      } finally {
        setImporting(false);
        setImportProgress(null);
      }
    },
    [addUnderlayFromFile],
  );

  const handlePickPdfPages = useCallback(
    async (choices: BoardSnapshotPdfChoice[]) => {
      if (choices.length === 0) return;
      setImporting(true);
      setImportProgress({ current: 0, total: choices.length });
      try {
        for (let i = 0; i < choices.length; i += 1) {
          const choice = choices[i];
          setImportProgress({ current: i + 1, total: choices.length });
          const safe = choice.label.replace(/[^\w.-]+/g, '_').slice(0, 80);
          const file = await renderPdfPageToFile(choice.url, choice.pageNumber, `${safe}.jpg`);
          await addUnderlayFromFile(file, choice.label);
        }
        setSnapshotOpen(false);
      } catch (err) {
        console.error('[FloorPlanEditor] PDF capture failed:', err);
        window.alert('Could not add one or more PDF pages to the floor plan.');
      } finally {
        setImporting(false);
        setImportProgress(null);
      }
    },
    [addUnderlayFromFile],
  );

  const handleHtmlCapture = useCallback(
    async (file: File) => {
      const i = htmlCaptureCountRef.current;
      htmlCaptureCountRef.current += 1;
      const viewLabel = i === 0 ? 'snapshot' : `snapshot ${i + 1}`;
      const versionBit = htmlCaptureDoc?.versionLabel ? ` · ${htmlCaptureDoc.versionLabel}` : '';
      const label = htmlCaptureDoc ? `${htmlCaptureDoc.title}${versionBit} · ${viewLabel}` : file.name;
      setImporting(true);
      try {
        await addUnderlayFromFile(file, label);
      } catch (err) {
        console.error('[FloorPlanEditor] model capture failed:', err);
        window.alert('Could not add that model capture to the floor plan.');
      } finally {
        setImporting(false);
      }
    },
    [addUnderlayFromFile, htmlCaptureDoc],
  );

  const applyFootprint = () => {
    const widthIn = parseDimensionToInches(widthDraft, 'ft');
    const depthIn = parseDimensionToInches(depthDraft, 'ft');
    if (!widthIn || !depthIn) return;
    const next = resizeFootprint(draft, widthIn, depthIn);
    commit(next);
    setWidthDraft(formatInchesFtIn(next.footprint.widthIn));
    setDepthDraft(formatInchesFtIn(next.footprint.depthIn));
  };

  const handleExportPdf = async (paper: FloorPlanPdfPaper) => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      await downloadFloorPlanPdf(draftRef.current, paper);
    } catch (err) {
      console.error('[FloorPlanEditor] PDF export failed:', err);
      window.alert('Could not export this floor plan as a PDF.');
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (e.key === 'Escape') {
        if (scaleRef) {
          e.preventDefault();
          e.stopPropagation();
          setScaleRef(null);
          return;
        }
        if (tool === 'wall' || tool === 'rect') return;
        e.preventDefault();
        e.stopPropagation();
        setTool('select');
        setPlaceCatalogId(null);
        setSelected(null);
        return;
      }
      if (!typing && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        e.stopPropagation();
        deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, deleteSelected, undo, redo, tool, scaleRef]);

  if (!open) return null;

  return (
    <div className="fp-editor" role="dialog" aria-label="Floor plan">
      <div className="fp-editor-stage">
        <FloorPlanCanvas
          key={viewEpoch}
          data={draft}
          tool={tool}
          thickness={thickness}
          placeCatalogId={placeCatalogId}
          selected={selected}
          onSelect={setSelected}
          onChange={setDraft}
          onBeginEdit={beginEdit}
          onToolChange={(next) => {
            setTool(next);
            if (next !== 'place') setPlaceCatalogId(null);
          }}
          scaleRef={scaleRef}
          onScaleRefChange={setScaleRef}
          snapEnabled={snapEnabled}
          presence={presence}
        />
        {importing && (
          <div className="fp-importing">
            Adding image
            {importProgress ? ` ${importProgress.current}/${importProgress.total}` : '…'}
          </div>
        )}
        <FloorPlanLayers
          underlays={draft.underlays || []}
          selectedId={selected?.kind === 'underlay' ? selected.id : null}
          onSelect={(id) => {
            setTool('select');
            setSelected({ kind: 'underlay', id });
          }}
          onToggleHidden={(id) => {
            commit({
              ...draft,
              underlays: (draft.underlays || []).map((entry) =>
                entry.id === id ? setUnderlayHidden(entry, !entry.hidden) : entry,
              ),
            });
          }}
        />
        <FloorPlanInspector
          data={draft}
          selected={selected}
          onChange={commit}
          onLiveChange={setDraft}
          onDelete={deleteSelected}
          scaleRef={scaleRef}
          onStartScaleRef={() => {
            if (!selected || selected.kind !== 'underlay') return;
            setTool('select');
            setScaleRef({ underlayId: selected.id, a: null, b: null });
          }}
          onCancelScaleRef={() => setScaleRef(null)}
          snapEnabled={snapEnabled}
          onRotateItem={() => {
            if (!selected || selected.kind !== 'item') return;
            commit({
              ...draft,
              items: draft.items.map((item) => (item.id === selected.id ? rotateItem(item) : item)),
            });
          }}
        />
      </div>

      <FloorPlanToolbar
        tool={tool}
        thickness={thickness}
        placeCatalogId={placeCatalogId}
        catalogGroup={catalogGroup}
        title={draft.title || ''}
        widthDraft={widthDraft}
        depthDraft={depthDraft}
        onToolChange={(next) => {
          setTool(next);
          if (next !== 'place') setPlaceCatalogId(null);
        }}
        onThicknessChange={setThickness}
        onCatalogGroupChange={setCatalogGroup}
        onPlaceCatalog={(id) => {
          setPlaceCatalogId(id);
          setTool('place');
        }}
        onTitleChange={(title) => setDraft({ ...draft, title })}
        onWidthChange={setWidthDraft}
        onDepthChange={setDepthDraft}
        onApplyFootprint={applyFootprint}
        onAddTrace={timberProjectId ? () => setSnapshotOpen(true) : undefined}
        onAddImages={(files) => void handleAddImages(files)}
        importing={importing}
        snapEnabled={snapEnabled}
        onExportPdf={(paper) => void handleExportPdf(paper)}
        exportingPdf={exportingPdf}
        onToggleSnap={() => {
          setSnapEnabled((prev) => {
            const next = !prev;
            saveSnapEnabled(next);
            return next;
          });
        }}
        onCancel={() => {
          flushAutosave();
          onClose();
        }}
        onDone={() => {
          flushAutosave();
          onSubmit(draftRef.current);
        }}
      />

      {timberProjectId && (
        <BoardSnapshotPicker
          open={snapshotOpen}
          projectId={timberProjectId}
          importing={importing}
          importProgress={importProgress}
          onClose={() => {
            if (!importing) setSnapshotOpen(false);
          }}
          onPickPdfPages={(choices) => void handlePickPdfPages(choices)}
          onPickModel={(choice) => {
            htmlCaptureCountRef.current = 0;
            setSnapshotOpen(false);
            setHtmlCaptureDoc(choice);
          }}
        />
      )}

      {htmlCaptureDoc && (
        <HtmlViewerModal
          url={htmlCaptureDoc.url}
          fetchUrl={htmlCaptureDoc.fetchUrl}
          title={htmlCaptureDoc.title}
          versionLabel={htmlCaptureDoc.versionLabel}
          captureMode
          onCapture={(file) => void handleHtmlCapture(file)}
          onClose={() => setHtmlCaptureDoc(null)}
        />
      )}
    </div>
  );
};

export default FloorPlanEditor;
